import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { useClipboardHooks } from '../../ClipboardHooksContext';
import LinkCursorSelection from '../../LinkCursorSelection';
import QuillComponent from '../../QuillComponent';
import { useQuill, withQuillContextProvider } from '../../QuillContext';
import EmojiAutocompleter from '../../controls/EmojiAutocompleter';
import MentionPicker from '../../controls/MentionPicker';
import SlashCommandMenu from '../../controls/SlashCommandMenu';
import EditorToolbar from '../../controls/toolbar/EditorToolbar';
import useLinkShortcut from '../../useLinkShortcut';
import UserSelectContainer from '../../utils/UserSelectContainer';
import * as styles from './TableCellEditor.module.scss';
import useCellKeyboardShortcuts from './useCellKeyboardShortcuts';

const DEFAULT_CONTENT = [{ insert: '\n' }];

interface TableCellEditorProps {
  focused: boolean;
  readOnly: boolean;
  containerClassName?: string;
  initialContent?: Delta;
  onContentChange: (delta: Delta) => void;
  onSelectionChange: (range: { index: number; length: number } | null) => void;
  isEmbedded: boolean;
}

const TableCellEditor = ({
  focused,
  readOnly,
  containerClassName,
  initialContent,
  onContentChange,
  onSelectionChange,
  isEmbedded,
}: TableCellEditorProps) => {
  const [isInited, setIsInited] = useState(focused);
  const contentRef = useRef(initialContent || DEFAULT_CONTENT);
  const { ancestorQuill, quill } = useQuill();

  useEffect(() => {
    if (focused) {
      setIsInited(true);
    } else if (quill) {
      quill.changeActiveBlot(null);
    }
  }, [focused, quill]);

  useLinkShortcut();
  useCellKeyboardShortcuts();
  const clipboardHooks = useClipboardHooks();

  return (
    <UserSelectContainer as="div" className={styles.container}>
      {quill && ancestorQuill && focused && (
        <EditorToolbar readOnly={readOnly} portal={ancestorQuill.container} />
      )}
      {isInited && quill && (
        <>
          <MentionPicker quill={quill} readOnly={false} />
          <EmojiAutocompleter quill={quill} readOnly={false} />
        </>
      )}
      <QuillComponent
        noTitleLine
        disallowOrphanFormats={['diff', 'comment']}
        content={contentRef.current}
        namespace={isEmbedded ? 'embeddedTableCell' : 'tableCell'}
        contentPlaceholder=""
        containerClassName={containerClassName}
        readOnly={readOnly}
        moduleConfig={{
          prefixListener: true,
          history: {
            parentQuill: ancestorQuill,
          },
          clipboard: {
            clipboardHooks,
          },
        }}
        theme="tableCellEditorTheme"
        onSelectionChange={onSelectionChange}
        onTextChange={(change: Delta, _oldContent: Delta, source: string) => {
          if (source === Quill.sources.USER) {
            onContentChange(change);
          }
        }}
      />
      {isInited && quill && (
        <>
          <SlashCommandMenu
            quill={quill}
            readOnly={false}
            imageTooltip={'Paste or drag/drop\nthe image into\nthis cell'}
          />
          <LinkCursorSelection />
        </>
      )}
    </UserSelectContainer>
  );
};

export default withQuillContextProvider(TableCellEditor);
